// Turning the Leads & Bookings filter controls into a GET /v1/captures query,
// and turning an empty result back into a sentence that is actually true.
//
// This is the query-shaped counterpart to captureFields.ts, which is the
// row-shaped one. Same reason for the split: everything here is a pure function
// over plain values, so the parts that have historically gone wrong — a date
// sent in the wrong units, a filter that silently drops a row, an empty list
// captioned as "no match" when it is really "nothing recorded" — are pinned by
// tests instead of by looking at the screen.
//
// THE FIVE SERVER FILTERS, verified against api-staging rather than read from
// source: agentId (number), did (string), syncStatus (enum), startDate, endDate.
// They intersect — agentId=22&syncStatus=LOCAL_ONLY returns the 2 rows that are
// both, not the 5 that are either.
//
// ON DATES, AND WHY THIS FILE SENDS EPOCH MILLISECONDS.
//
// The server accepts three forms — `2026-08-20`, `2026-08-20T09:30:00`, and
// epoch millis — and its own refusal message names epoch millis as the canonical
// one ("the same number capturedAt comes back as"). That alone would be reason
// enough, but it is not the real reason.
//
// The real reason is that a bare `2026-08-20` is resolved to an instant by the
// SERVER, in the server's timezone, while every timestamp on this screen is
// rendered by `formatCapturedAt` in the READER's timezone via toLocaleString.
// The moment those two zones differ, a row displayed as "20 Aug, 7:52 pm" drops
// out of a filter the reader set to 20 Aug, and the screen contradicts itself
// with no error anywhere. Converting the picked day to epoch millis HERE, from
// the browser's own local midnight, makes the filter agree with the displayed
// timestamps by construction, in every timezone, without either side having to
// know what the other's clock is set to.
//
// Both ends are inclusive instants — verified: endDate set to the exact
// capturedAt of the oldest row returns that row, one millisecond earlier
// returns nothing. So the end of a range is local 23:59:59.999, not the next
// midnight, which would have pulled in a row captured at exactly 00:00:00.
import type { CaptureType, CaptureSyncStatus } from 'api/services/captures.service';

/**
 * What the controls hold.
 *
 * Deliberately the UI's own vocabulary, not the wire's: dates are the
 * `YYYY-MM-DD` an <input type="date"> gives and takes, `did` is whatever was
 * typed including the spaces and plus signs, and `agentId` is null rather than
 * 0 for "any" so a falsy check cannot confuse "no agent filter" with an agent
 * whose id is 0. captureQueryParams is the single place this becomes a query.
 */
export interface CaptureFilters {
  /**
   * One type, or a comma-separated set for a tab that spans several.
   *
   * Widened from `CaptureType | ''` when Bookings became a tab: it covers
   * BOOKING, BOOKING_RESCHEDULED and BOOKING_CANCELLED, and the server takes
   * them comma-separated. A single value behaves exactly as before.
   */
  captureType: string;
  agentId: number | null;
  /** As typed. '' is no filter; 'none' means captures with no DID recorded. */
  did: string;
  syncStatus: CaptureSyncStatus | '';
  /** `YYYY-MM-DD`, or '' for open-ended. */
  startDate: string;
  endDate: string;
  /**
   * A field INSIDE the captured payload, and the value it must hold.
   *
   * These are a pair: the server refuses one without the other, because half a
   * pair matches nothing and an empty page reads as "no such leads" rather than
   * as an unfinished filter. They are kept as two strings rather than one
   * `key=value` string so the key can come from a picker while the value stays
   * free text.
   *
   * The key is deliberately NOT an enum. Atmantan's leads carry
   * `primary_wellness_goal` and `lead_quality`; the hospital's carry `purpose`
   * and `disposition`. One screen serves both, so the options come from the
   * data on screen.
   */
  fieldKey: string;
  fieldValue: string;
}

export const EMPTY_FILTERS: CaptureFilters = {
  captureType: '', agentId: null, did: '', syncStatus: '', startDate: '', endDate: '',
  fieldKey: '', fieldValue: '',
};

/** The sentinel the server accepts for "captures with no DID recorded". Case-insensitive there; lowercase here. */
export const DID_NONE = 'none';

/**
 * The valid sync statuses, in the order the filter offers them.
 *
 * LOCAL_ONLY was added to the server today. It means the agent is deliberately
 * configured NOT to sync — a setting somebody chose — which is a different fact
 * from NOT_CONFIGURED, where nobody has connected a CRM at all. Neither is a
 * failure, and neither is grouped with FAILED here; the ones that do want acting
 * on are last so the list reads best-to-worst rather than alphabetically.
 *
 * INCOMPLETE sits at the head of those because it is the most fixable: the lead
 * is here, it is whole enough to read, and one missing field is between it and
 * the CRM. It is also the only one whose fix is a conversation with the business
 * about what its agent should be asking for.
 */
export const SYNC_STATUS_OPTIONS: { value: CaptureSyncStatus; label: string }[] = [
  { value: 'SYNCED', label: 'In CRM' },
  { value: 'LOCAL_ONLY', label: 'Kept here only' },
  { value: 'NOT_CONFIGURED', label: 'No CRM connected' },
  { value: 'INCOMPLETE', label: 'Missing required fields' },
  { value: 'QUEUED', label: 'Retrying' },
  { value: 'FAILED', label: 'Not in CRM' },
];

/** A `YYYY-MM-DD` as local midnight, in epoch millis. Null when it is not a date. */
export function localDayStartMs(ymd: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // Constructed with local-time arguments on purpose — `new Date('2026-08-20')`
  // parses as UTC midnight, which is the off-by-one this whole approach exists
  // to avoid.
  const dt = new Date(y, mo - 1, d, 0, 0, 0, 0);
  // Round-tripped, so 2026-02-31 is rejected rather than silently becoming March.
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt.getTime();
}

/** A `YYYY-MM-DD` as the last local millisecond of that day. Both ends are inclusive. */
export function localDayEndMs(ymd: string): number | null {
  const start = localDayStartMs(ymd);
  if (start === null) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())!;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  return dt.getTime();
}

/**
 * What to put in `did`, or null to send nothing.
 *
 * The server wants at least four digits in any format — 00919228034177,
 * +91 92280 34177 and 9228034177 all match the same line — or the literal
 * `none`. Punctuation is stripped here so the value on the wire is the same
 * whichever way it was typed, which keeps the URL stable when the same search
 * is shared.
 *
 * A too-short entry is NOT rejected here. It is passed on and the server's own
 * refusal is shown verbatim, because duplicating the four-digit rule in the
 * client is how the two drift apart: the client would go on rejecting a value
 * the server had started accepting, and nothing would say why.
 */
export function didQueryValue(raw: string): string | null {
  const trimmed = String(raw || '').trim();
  if (trimmed === '') return null;
  if (trimmed.toLowerCase() === DID_NONE) return DID_NONE;
  const digits = trimmed.replace(/[^\d]/g, '');
  // Nothing numeric at all still goes to the server, unchanged, so its message
  // ("did 'abcd' is not a number…") names what was actually typed.
  return digits === '' ? trimmed : digits;
}

/** True when this filter set asks about a SPECIFIC line, rather than about the absence of one. */
export function isSpecificDid(filters: CaptureFilters): boolean {
  const v = didQueryValue(filters.did);
  return v !== null && v !== DID_NONE;
}

/** Everything the query string should carry, blanks omitted. */
export interface CaptureQueryParams {
  /**
   * One type, or several comma-separated — the Bookings tab sends three.
   * Widened alongside CaptureFilters.captureType; the server parses either.
   */
  captureType?: string;
  agentId?: number;
  did?: string;
  syncStatus?: CaptureSyncStatus;
  startDate?: number;
  endDate?: number;
  fieldKey?: string;
  fieldValue?: string;
}

/**
 * The filters as query parameters.
 *
 * Absent beats blank throughout. `captureType=` reaches the server as an empty
 * string, which it happens to tolerate today, but "no filter" genuinely means
 * "no parameter" and that is the form that survives a stricter parser — the
 * same reasoning the existing captureType filter already used.
 */
export function captureQueryParams(filters: CaptureFilters): CaptureQueryParams {
  const params: CaptureQueryParams = {};
  if (filters.captureType) params.captureType = filters.captureType;
  if (filters.agentId !== null && Number.isFinite(filters.agentId)) {
    params.agentId = Number(filters.agentId);
  }
  const did = didQueryValue(filters.did);
  if (did !== null) params.did = did;
  if (filters.syncStatus) params.syncStatus = filters.syncStatus;

  const start = localDayStartMs(filters.startDate);
  if (start !== null) params.startDate = start;
  const end = localDayEndMs(filters.endDate);
  if (end !== null) params.endDate = end;

  // Both or neither. The server returns 400 for half a pair, and sending one
  // alone would turn a half-finished filter into an error toast; sending
  // neither leaves the list unfiltered until the pair is complete, which is
  // what someone mid-typing actually wants.
  const fieldKey = filters.fieldKey.trim();
  const fieldValue = filters.fieldValue.trim();
  if (fieldKey && fieldValue) {
    params.fieldKey = fieldKey;
    params.fieldValue = fieldValue;
  }

  return params;
}

/** True when anything is narrowing the list. Drives whether "Clear" is offered at all. */
export function hasAnyFilter(filters: CaptureFilters): boolean {
  return Object.keys(captureQueryParams(filters)).length > 0;
}

/**
 * The two tabs this screen splits into, and what each one covers.
 *
 * <h3>Two, not three</h3>
 * The ask was to separate Leads, Bookings and Messages. The first two belong
 * together as tabs: same source, same filters, same row shape, so switching is
 * instant and the saved views stay shared. Messages does not — it lives under
 * /whatsapp/*, a different source none of the filters on this screen apply to,
 * and a tab that silently ignored the filter bar above it would teach people
 * the controls are unreliable. It is a link out instead.
 *
 * <h3>Bookings means all three booking states</h3>
 * A tab labelled "Bookings" that showed only new ones — hiding every reschedule
 * and cancellation — would be worse than no tab, because nothing on screen
 * would say what was missing. The server takes a comma-separated captureType
 * for exactly this.
 */
export interface CaptureTab {
  id: 'all' | 'leads' | 'bookings';
  label: string;
  /** '' means every type. */
  captureType: string;
  /** What this tab's rows are about, for the empty state. */
  noun: string;
}

export const CAPTURE_TABS: CaptureTab[] = [
  { id: 'all', label: 'Everything', captureType: '', noun: 'capture' },
  { id: 'leads', label: 'Leads', captureType: 'LEAD', noun: 'lead' },
  {
    id: 'bookings',
    label: 'Bookings',
    captureType: 'BOOKING,BOOKING_RESCHEDULED,BOOKING_CANCELLED',
    noun: 'booking',
  },
];

/** Which tab a filter value is showing. Unrecognised narrowing reads as Everything. */
export function activeTab(captureType: string): CaptureTab {
  return CAPTURE_TABS.find((t) => t.captureType === captureType) ?? CAPTURE_TABS[0];
}

/**
 * The saved views offered above the filters.
 *
 * <h3>Only what is computable today</h3>
 * Four quick views were asked for: Needs Attention, CRM Failed, Callback Due
 * and Unassigned. Only the CRM ones can be built — the other two need a lead to
 * have an owner and a due time, and nothing in the schema carries either. A
 * preset that silently matched everything would be worse than its absence, so
 * they are not offered until there is something behind them.
 *
 * <h3>Why these are filters and not a new concept</h3>
 * Each one is an ordinary CaptureFilters value. That keeps one source of truth
 * for what is on screen: the chips, the empty states and the clear-all all keep
 * working without knowing a preset was involved.
 *
 * "Not in CRM" is deliberately FAILED alone. NOT_CONFIGURED is not a failure —
 * plenty of tenants run on cpass with no CRM at all, and sweeping those in here
 * would tell them something is broken every time the agent worked correctly.
 */
export interface CaptureView {
  id: string;
  label: string;
  /** What the reader is being shown, for a title attribute. */
  hint: string;
  filters: CaptureFilters;
}

export const CAPTURE_VIEWS: CaptureView[] = [
  {
    id: 'failed',
    label: 'Not in CRM',
    hint: 'Captures the CRM rejected. The capture itself is safe either way.',
    filters: { ...EMPTY_FILTERS, syncStatus: 'FAILED' },
  },
  {
    id: 'retrying',
    label: 'Retrying',
    hint: 'Queued for the CRM and not there yet. These usually land by themselves.',
    filters: { ...EMPTY_FILTERS, syncStatus: 'QUEUED' },
  },
  {
    id: 'today',
    label: 'Today',
    hint: 'Everything captured today, whatever its CRM state.',
    filters: { ...EMPTY_FILTERS, startDate: todayYmd(), endDate: todayYmd() },
  },
];

/** Local today as `YYYY-MM-DD` — the same shape the date inputs use. */
export function todayYmd(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Whether the screen is currently showing exactly this view.
 *
 * Compared on the query the filters produce rather than on the objects, so a
 * view stays "on" when the reader arrives at the same selection through the
 * ordinary controls — which is what a reader would expect, having done the same
 * thing by hand.
 */
export function isViewActive(view: CaptureView, filters: CaptureFilters): boolean {
  const a = captureQueryParams(view.filters) as Record<string, unknown>;
  const b = captureQueryParams(filters) as Record<string, unknown>;
  // Keys from both sides, de-duplicated with indexOf rather than a Set: this
  // build target refuses to iterate one, and the lists here are a handful of
  // filter names.
  const keys = Object.keys(a).concat(Object.keys(b).filter((k) => !(k in a)));
  return keys.every((k) => String(a[k] ?? '') === String(b[k] ?? ''));
}

/**
 * A range whose end precedes its start.
 *
 * The server accepts it and answers zero — verified — which on screen is
 * indistinguishable from a genuine no-match, so it is caught here and said
 * plainly instead. Compared as picked days rather than as instants, because
 * that is what the reader chose.
 */
export function isBackwardsRange(filters: CaptureFilters): boolean {
  const s = localDayStartMs(filters.startDate);
  const e = localDayStartMs(filters.endDate);
  return s !== null && e !== null && e < s;
}

// ---------------------------------------------------------------------------
// Agent labels
// ---------------------------------------------------------------------------

/** The minimum of an agent this module needs. Anything with an id and a name fits. */
export interface AgentLike { id?: number; name?: string }

export type AgentOption = {
  value: number;
  /** `Name (id)`, or `Agent 22` when no name is known. */
  label: string;
  /** True when the id came only from the captures, with no agent record behind it. */
  unresolved: boolean;
};

/** Agents by id, from whatever the agents endpoint returned. */
export function agentsById(agents: AgentLike[] | null | undefined): Map<number, string> {
  const byId = new Map<number, string>();
  for (const a of agents || []) {
    const id = Number(a?.id);
    const name = String(a?.name ?? '').trim();
    if (Number.isFinite(id) && name !== '') byId.set(id, name);
  }
  return byId;
}

/**
 * How an agent is named everywhere on this screen: `Name (id)`.
 *
 * The id is never replaced by the name. agentId is what the API filters on and
 * what the row's own footer has always shown, so somebody who knows an agent as
 * "22" and somebody who knows it as "IH_26 — Inbound" both have to be able to
 * find it, in the dropdown and in the list, without a lookup table in their
 * head. When the name is unknown the id stands alone rather than rendering as
 * "undefined (22)" or as a blank.
 */
export function agentLabel(id: number | null | undefined, byId: Map<number, string>): string {
  if (id === null || id === undefined || !Number.isFinite(Number(id))) return 'Unknown agent';
  const n = Number(id);
  const name = byId.get(n);
  return name ? `${name} (${n})` : `Agent ${n}`;
}

/**
 * The options the agent filter offers.
 *
 * Built from the UNION of the agents endpoint and the agent ids actually
 * present in the captures, which is the whole point. An agent that has been
 * archived, disabled, or moved out of the caller's organisation drops off the
 * agents list while its captures stay in the table forever. A dropdown built
 * from the name lookup alone would quietly not offer it, and every lead it ever
 * took would become unreachable through a control that looked complete.
 *
 * The currently selected id is included too, even when it appears in neither —
 * arriving on a URL with ?agentId=99 must leave the control showing 99 rather
 * than silently resetting itself to "any agent" while the list stays filtered.
 *
 * Named agents sort first and alphabetically; the unresolved ones follow by id,
 * flagged, so nothing looks like a name that is not one.
 */
export function agentOptions(
  agents: AgentLike[] | null | undefined,
  idsInData: Array<number | null | undefined>,
  selectedId: number | null,
): AgentOption[] {
  const byId = agentsById(agents);
  const ids = new Set<number>();
  // forEach, not `for…of byId.keys()`: this project's tsconfig targets es5
  // without downlevelIteration, so iterating a Map's iterator is a compile
  // error. ts-jest happily runs it — the CI build is what catches it.
  byId.forEach((_name, id) => ids.add(id));
  for (const id of idsInData || []) {
    if (id !== null && id !== undefined && Number.isFinite(Number(id))) ids.add(Number(id));
  }
  if (selectedId !== null && Number.isFinite(Number(selectedId))) ids.add(Number(selectedId));

  return Array.from(ids)
    .map((value) => ({ value, label: agentLabel(value, byId), unresolved: !byId.has(value) }))
    .sort((a, b) => {
      if (a.unresolved !== b.unresolved) return a.unresolved ? 1 : -1;
      return a.unresolved ? a.value - b.value : a.label.localeCompare(b.label);
    });
}

// ---------------------------------------------------------------------------
// The active filter set, as chips
// ---------------------------------------------------------------------------

/** One active filter, named in the reader's terms, with the key that clears it. */
export type FilterChip = { key: keyof CaptureFilters; label: string };

/**
 * What is currently narrowing the list, spelled out.
 *
 * The controls themselves already show their own values, but a date picker
 * reading "2026-08-20" and a select reading "In CRM" do not add up to a sentence
 * anybody reads as "these four things are hiding rows from you". The chips are
 * that sentence, and each one carries the key that removes just itself, so
 * undoing one filter never means clearing them all and starting again.
 */
export function activeFilterChips(
  filters: CaptureFilters,
  byId: Map<number, string>,
): FilterChip[] {
  const chips: FilterChip[] = [];
  // captureType is deliberately NOT a chip any more. It is the tab now, and a
  // tab is what you are looking at rather than something hiding rows from you.
  // As a chip it would carry a remove button that silently moved you to
  // Everything — an undo for a thing nobody filtered.
  if (filters.agentId !== null) chips.push({ key: 'agentId', label: agentLabel(filters.agentId, byId) });

  const did = didQueryValue(filters.did);
  if (did === DID_NONE) chips.push({ key: 'did', label: 'No DID recorded' });
  else if (did !== null) chips.push({ key: 'did', label: `DID ${did}` });

  if (filters.syncStatus) {
    const found = SYNC_STATUS_OPTIONS.find((o) => o.value === filters.syncStatus);
    chips.push({ key: 'syncStatus', label: found ? found.label : String(filters.syncStatus) });
  }
  if (localDayStartMs(filters.startDate) !== null) chips.push({ key: 'startDate', label: `From ${filters.startDate}` });
  if (localDayEndMs(filters.endDate) !== null) chips.push({ key: 'endDate', label: `Until ${filters.endDate}` });

  // One chip for the pair, keyed on fieldKey. Removing it must clear BOTH — a
  // leftover value with no key is a filter that cannot be seen and cannot be
  // removed, and the server would refuse it anyway.
  if (filters.fieldKey.trim() && filters.fieldValue.trim()) {
    chips.push({ key: 'fieldKey', label: `${filters.fieldKey} is ${filters.fieldValue}` });
  }
  return chips;
}

/**
 * One filter removed, the rest untouched.
 *
 * <p>The field filter is a PAIR behind a single chip, so removing it clears
 * both halves. Clearing only the key would leave a value that shows nothing,
 * removes nothing, and that the server refuses on sight.
 */
export function withoutFilter(filters: CaptureFilters, key: keyof CaptureFilters): CaptureFilters {
  if (key === 'fieldKey' || key === 'fieldValue') {
    return { ...filters, fieldKey: '', fieldValue: '' };
  }
  return { ...filters, [key]: key === 'agentId' ? null : '' } as CaptureFilters;
}

// ---------------------------------------------------------------------------
// The URL
// ---------------------------------------------------------------------------
//
// Filters live in the query string. This screen's whole purpose is answering a
// question somebody asked — "what did agent 24 take last Tuesday that never
// reached the CRM" — and the answer is worth sending to the person who asked.
// In the URL it survives a reload, works with the back button after opening a
// capture, and pastes into a chat as itself. Held in component state it is a
// screenshot.
//
// The DAY is what goes in the URL, not the epoch millis it becomes. A link
// carrying local-midnight millis would resolve to a different day for a reader
// in another timezone; ?startDate=2026-08-20 means the same day everywhere and
// is converted per-reader on arrival. The units on the WIRE and the units in the
// URL are answering different questions.
//
// `page` is deliberately absent. Page 4 of a filter set is not a thing anybody
// means to share, and restoring it would land the reader on an empty list every
// time the underlying data had shrunk since the link was made.

const URL_KEYS: Array<[keyof CaptureFilters, string]> = [
  ['captureType', 'type'],
  ['agentId', 'agentId'],
  ['did', 'did'],
  ['syncStatus', 'sync'],
  ['startDate', 'from'],
  ['endDate', 'to'],
  ['fieldKey', 'field'],
  ['fieldValue', 'is'],
];

/** The filters as URL parameters. Only what is set, so a clean screen has a clean URL. */
export function filtersToSearchParams(filters: CaptureFilters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, key] of URL_KEYS) {
    const v = filters[field];
    if (v === null || v === undefined || v === '') continue;
    out[key] = String(v);
  }
  return out;
}

/**
 * Filters read back off a URL.
 *
 * Everything is validated against what the controls can actually represent, and
 * anything else is dropped rather than carried: a hand-edited ?sync=BANANA that
 * reached the request would come back as a refusal on first paint, which reads
 * as the screen being broken rather than as the link being wrong. A dropped
 * value leaves that filter simply unset.
 */
export function filtersFromSearchParams(
  get: (key: string) => string | null,
  knownTypes: readonly string[],
): CaptureFilters {
  const raw = (k: string) => (get(k) || '').trim();

  const type = raw('type');
  const sync = raw('sync');
  const agent = Number(raw('agentId'));
  const isYmd = (s: string) => localDayStartMs(s) !== null;
  const from = raw('from');
  const to = raw('to');
  const fieldKey = raw('field');
  const fieldValue = raw('is');

  return {
    // Every member must be known, not just the whole string. A tab sends a
    // comma-separated set, and checking the joined value against the list of
    // single types silently dropped it — the URL carried the Bookings filter
    // while the screen showed Everything.
    captureType: type !== '' && type.split(',').every((t) => knownTypes.includes(t.trim()))
      ? type
      : '',
    agentId: raw('agentId') !== '' && Number.isFinite(agent) && Number.isInteger(agent) ? agent : null,
    did: raw('did'),
    syncStatus: SYNC_STATUS_OPTIONS.some((o) => o.value === sync) ? (sync as CaptureSyncStatus) : '',
    startDate: isYmd(from) ? from : '',
    endDate: isYmd(to) ? to : '',
    // Same shape the server validates, applied here so a hand-edited
    // ?field=a'].b never leaves the browser. A key it would refuse is dropped
    // rather than carried, which leaves the filter unset instead of painting a
    // 400 over the first render.
    fieldKey: /^[A-Za-z0-9_]{1,64}$/.test(fieldKey) ? fieldKey : '',
    fieldValue: /^[A-Za-z0-9_]{1,64}$/.test(fieldKey) ? fieldValue : '',
  };
}

// ---------------------------------------------------------------------------
// Empty, and why
// ---------------------------------------------------------------------------

/**
 * Why the list is empty. The distinction the DID filter exists to preserve.
 *
 * THE TRAP. `?did=9228034177` returns zero rows, and that is the correct
 * answer — every capture in the table has `did = NULL`, because they all
 * predate the column. Rendered as a plain empty list, that reads as "no capture
 * came from this line", which is a claim about the phone line. The truth is a
 * claim about the DATABASE: no capture has a DID recorded at all, so no DID
 * search can ever match one, and the entire history has just been hidden by a
 * control that looks like it is working.
 *
 * The two are told apart by asking the server rather than by guessing. When a
 * specific-DID search comes back empty, the screen counts the same filter set
 * with `did=none` and with no DID filter at all. If every capture in range has
 * no DID, the filter cannot match and the screen says so. If some captures do
 * carry a DID, then zero genuinely means no match, and it says that instead —
 * and can say how many DID-bearing captures it looked at.
 *
 * This is not a migration gap that will close on its own. Agent 21's tool schema
 * has no route/call_from/call_to at all, so its captures can never carry a DID.
 * A permanently mixed table is the normal state, which is why the wording is
 * decided per-search from live counts and never from a one-off flag.
 */
export type EmptyReason =
  | { kind: 'NO_CAPTURES' }
  | { kind: 'FILTERED_OUT' }
  | { kind: 'BACKWARDS_RANGE' }
  | { kind: 'DID_NEVER_RECORDED'; inRange: number }
  | { kind: 'DID_NO_MATCH'; withDid: number };

/** What the counts behind a specific-DID search came back as. Null while unknown. */
export interface DidProbe {
  /** Captures matching the other filters, with no DID filter applied. */
  total: number;
  /** Of those, how many have no DID recorded. */
  noDid: number;
}

/**
 * The reason an empty list is empty.
 *
 * @param filters   what the controls hold
 * @param anyFilter whether anything is narrowing the list at all
 * @param probe     the two counts, when a specific DID was searched and found nothing
 */
export function emptyReason(
  filters: CaptureFilters,
  anyFilter: boolean,
  probe: DidProbe | null,
): EmptyReason {
  if (isBackwardsRange(filters)) return { kind: 'BACKWARDS_RANGE' };

  if (isSpecificDid(filters) && probe) {
    // Nothing in range at all — the DID is not what emptied the list, so this
    // must not claim anything about DIDs.
    if (probe.total > 0) {
      const withDid = probe.total - probe.noDid;
      if (withDid <= 0) return { kind: 'DID_NEVER_RECORDED', inRange: probe.total };
      return { kind: 'DID_NO_MATCH', withDid };
    }
  }

  return anyFilter ? { kind: 'FILTERED_OUT' } : { kind: 'NO_CAPTURES' };
}
